import {
  SUBSCRIPTION_STATUS_ACTIVE,
  YEARLY_PERIODICITY,
} from "@arbeidsark/shared"
import type { StripeSubscription } from "@arbeidsark/shared"
import type { SubscriptionPeriodicity } from "@arbeidsark/shared"
import { Elements } from "@stripe/react-stripe-js"
import type { StripeElementsOptions } from "@stripe/stripe-js"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import type Stripe from "stripe"
import styled, { useTheme } from "styled-components"
import { OutlineButton } from "~/components"
import { useLanguage } from "~/locale/TranslationProvider"
import { clearAuthToken } from "~/modules/authentication"
import { Loader } from "~/ur-react-components"
import { useTranslate } from "~/ur-react-hooks"
import { stripeLocaleLookup } from "~/utils/stripe"
import { trpc } from "~/utils/trpc"
import { stripePromise } from "../stripe"
import PaymentIntentEnterPaymentDetails from "./PaymentIntentEnterPaymentDetails"
import SelectSubscription from "./SelectSubscription"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 2rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};

  padding: 2rem 1.5rem;

  border-radius: 8px;

  width: 378px;
`

const Title = styled.h2`
  margin-bottom: 2rem;
  width: 450px;
  text-align: center;
`

const LogoutWrapper = styled.div`
  margin-top: 1rem;
`

export type BuyNewSubscriptionProps = {}

/**
 * This component handles the flow of buying a new subscription.
 *
 * Doing this depends on a number of things:
 *
 *  1. After the user selects periodicity, we attempt to create a subscription. If the user has an existing payment method, we try to pay immediately.
 *  2. If payment does not go through, we show the user a form to enter a new payment method.
 *  3. If the user already has an existing incomplete subscription (e.g. they started the flow but didn't complete it), we have to get them to enter their payment details again.
 */
const BuyNewSubscription: React.FC<BuyNewSubscriptionProps> = () => {
  const [step, setStep] = useState<
    "select" | "chose_existing_method_or_pay" | "pay"
  >("select")
  const [periodicity, setPeriodicity] =
    useState<SubscriptionPeriodicity>(YEARLY_PERIODICITY)
  const [paymentIntent, setPaymentIntent] = useState<Stripe.PaymentIntent>()
  const [subscription, setSubscription] = useState<StripeSubscription>()

  const [language] = useLanguage()
  const theme = useTheme()
  const navigate = useNavigate()

  const translations = useTranslate({
    title: "buy-new-subscription.title",
    enterPaymentTitle: "pay-for-subscription.title",
    enterPaymentSubmitText: "pay-for-subscription.purchase",
    logout: "common.log-out",
  })

  // TODO: Load existing incomplete subscription if exists
  const {
    data: existingIncompleteSubscription,
    isLoading: existingSubscriptionLoading,
    refetch,
  } = trpc.subscriptions.getIncompleteSubscriptionIfExists.useQuery(undefined, {
    enabled: false,
    // Make sure the query only runs once
    staleTime: Number.POSITIVE_INFINITY,
  })

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    if (!existingIncompleteSubscription) {
      return
    }

    // If we have an existing incomplete subscription, we need to get the user to
    // enter their payment details.
    const { subscription, paymentIntent } = existingIncompleteSubscription

    setSubscription(subscription)
    setPaymentIntent(paymentIntent)
    setStep("pay")
  }, [existingIncompleteSubscription])

  const { mutate: createNewSubscription, isLoading } =
    trpc.subscriptions.createNewSubscriptionForUser.useMutation({
      onSuccess({ subscription, paymentIntent }) {
        if (subscription.status === SUBSCRIPTION_STATUS_ACTIVE) {
          navigate("/subscription/payment-success")
          return
        }

        setPaymentIntent(paymentIntent)
        setSubscription(subscription)
        setStep("pay")
      },
    })

  const logout = useCallback(() => {
    clearAuthToken()
    navigate("/login")
  }, [navigate])

  if (existingSubscriptionLoading) {
    return (
      <Layout>
        <Card>
          <Loader.Spinner />
        </Card>
      </Layout>
    )
  }

  if (paymentIntent && !paymentIntent.client_secret) {
    return (
      <Layout>
        <Card>
          <h2>Client secret not available...</h2>
        </Card>
      </Layout>
    )
  }

  const options: StripeElementsOptions = {
    clientSecret: paymentIntent?.client_secret ?? undefined,
    appearance: {
      rules: {
        ".Input": {
          borderColor: theme.colors.gray70,
          borderRadius: "4px",
          outline: "none",
        },
        ".Input:focus": {
          border: "2px solid " + theme.colors.primary40,
          boxShadow: "none",
        },
      },
    },
    locale: stripeLocaleLookup[language],
  }

  return (
    <Layout>
      <Title>{translations.title}</Title>
      <Card>
        {step === "select" && (
          <>
            <SelectSubscription
              onNext={() => {
                if (!paymentIntent) {
                  createNewSubscription(periodicity)
                } else if (subscription?.periodicity !== periodicity) {
                  createNewSubscription(periodicity)
                } else {
                  setStep("pay")
                }
              }}
              value={periodicity}
              onChange={setPeriodicity}
              isLoading={isLoading}
            />
            <LogoutWrapper>
              <OutlineButton width="100%" onClick={() => logout()}>
                {translations.logout}
              </OutlineButton>
            </LogoutWrapper>
          </>
        )}
        {step === "pay" && isLoading && <Loader.Spinner />}
        {step === "pay" && !isLoading && (
          <Elements stripe={stripePromise} options={options}>
            <PaymentIntentEnterPaymentDetails
              title={translations.enterPaymentSubmitText}
              submitText={translations.enterPaymentSubmitText}
              redirectUrl={`${
                import.meta.env.VITE_APP_URL
              }/subscription/payment-success`}
              onBack={() => {
                setStep("select")
              }}
            />
          </Elements>
        )}
      </Card>
    </Layout>
  )
}

export default BuyNewSubscription
